import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Plus, Pencil, Trash, CheckSquare, MagnifyingGlass, Lightning } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';

const ROLE_LABELS = { admin: 'Administrator', supervizor: 'Supervizor', employee: 'Korisnik' };

const FALLBACK_VRSTA_POSLA_MAP = {
  pdv: 'PDV',
  prijava_radnika: 'Prijava radnika',
  odjava_radnika: 'Odjava radnika',
  poresko_uvjerenje: 'Zahtjev za poresko uvjerenje',
  registracija_drustva: 'Registracija društva',
  registracija_preduzetnika: 'Registracija preduzetnika',
  radna_dozvola_stranac: 'Radna dozvola za stranca',
  produzenje_radne_dozvole: 'Produženje radne dozvole',
  prijava_trgovine: 'Prijava trgovine',
  odjava_trgovine: 'Odjava trgovine',
  analize_vode_brisevi: 'Analize vode i brisevi',
  odobrenje_cigare: 'Zahtjev za odobrenje za cigare',
  kaznena_evidencija: 'Zahtjev iz kaznene evidencije',
  ostalo: 'Ostalo',
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [vrstaPoslaMap, setVrstaPoslaMap] = useState(FALLBACK_VRSTA_POSLA_MAP);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canCreate = (user.role === 'admin' || user.role === 'super_admin' || user.role === 'supervizor');

  const emptyForm = { naslov: '', opis: '', client_id: '', dodijeljeno: '', vrsta_posla: 'pdv', prioritet: 'srednji', hitnost: 'nije_hitno', rok: '', status: 'novo' };
  const [formData, setFormData] = useState(emptyForm);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [filterHitnost, setFilterHitnost] = useState('all');
  const [filterVrsta, setFilterVrsta] = useState('all');

  useEffect(() => { fetchTasks(); fetchClients(); fetchUsers(); fetchConfig(); }, []);

  const fetchTasks = async () => {
    try { const r = await api.get('/tasks'); setTasks(r.data); }
    catch { toast.error('Greška pri učitavanju zadataka'); }
    finally { setLoading(false); }
  };
  const fetchClients = async () => {
    try { const r = await api.get('/clients'); setClients(r.data); } catch {}
  };
  const fetchUsers = async () => {
    try { const r = await api.get('/users'); setUsers(r.data); } catch {}
  };
  const fetchConfig = async () => {
    try {
      const r = await api.get('/settings/config-lists');
      const vp = r.data.vrste_posla || [];
      if (vp.length > 0) {
        // Merge: keep fallback keys + add custom ones as label-only entries keyed by slug
        const map = { ...FALLBACK_VRSTA_POSLA_MAP };
        vp.forEach(label => {
          const key = label.toLowerCase().replace(/\s+/g, '_');
          map[key] = label;
        });
        setVrstaPoslaMap(map);
      }
    } catch {}
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.id === userId);
    return u ? u.name : '-';
  };

  const filteredTasks = tasks.filter(t => {
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (filterUser !== 'all' && t.dodijeljeno !== filterUser) return false;
    if (filterHitnost !== 'all' && (t.hitnost || 'nije_hitno') !== filterHitnost) return false;
    if (filterVrsta !== 'all' && (t.vrsta_posla || 'pdv') !== filterVrsta) return false;
    if (filterSearch) {
      const s = filterSearch.toLowerCase();
      if (!t.naslov?.toLowerCase().includes(s) && !t.opis?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // Sort: HITNO first
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const aH = a.hitnost === 'hitno' ? 0 : 1;
    const bH = b.hitnost === 'hitno' ? 0 : 1;
    return aH - bH;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const taskData = { ...formData, rok: formData.rok ? new Date(formData.rok).toISOString() : null };
      if (editingTask) {
        const response = await api.put(`/tasks/${editingTask.id}`, taskData);
        if (response.data?.moved_to_dnevnik) {
          toast.success('Zadatak završen i prebačen u Dnevnik rada');
        } else {
          toast.success('Zadatak ažuriran');
        }
      } else {
        await api.post('/tasks', taskData);
        toast.success('Zadatak kreiran');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Greška pri čuvanju zadatka');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      naslov: task.naslov || '',
      opis: task.opis || '',
      client_id: task.client_id || '',
      dodijeljeno: task.dodijeljeno || '',
      vrsta_posla: task.vrsta_posla || 'pdv',
      prioritet: task.prioritet || 'srednji',
      hitnost: task.hitnost || 'nije_hitno',
      rok: task.rok ? new Date(task.rok).toISOString().split('T')[0] : '',
      status: task.status || 'novo',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Da li ste sigurni?'))) return;
    try { await api.delete(`/tasks/${id}`); toast.success('Zadatak obrisan'); fetchTasks(); }
    catch { toast.error('Greška pri brisanju'); }
  };

  const resetForm = () => { setFormData(emptyForm); setEditingTask(null); };

  const getStatusColor = (s) => {
    switch (s) {
      case 'u_toku': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'zavrseno': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'ceka_klijenta': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };
  const getStatusLabel = (s) => {
    const l = { 'novo': 'Novo', 'u_toku': 'U toku', 'zavrseno': 'Završeno', 'ceka_klijenta': 'Čeka klijenta' };
    return l[s] || s;
  };
  const getPrioritetColor = (p) => {
    switch (p) {
      case 'visok': return 'bg-red-50 text-red-700 border-red-200';
      case 'srednji': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Zadaci</h3>
          <p className="text-sm text-slate-600 mt-1">
            {user.role === 'employee' ? 'Vaši zadaci' : 'Upravljanje zadacima'}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium" data-testid="task-add-button">
            <Plus size={20} weight="bold" className="mr-2" /> Novi zadatak
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4" data-testid="tasks-filters">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Pretraga..." className="pl-9 bg-white border-slate-300 text-sm" data-testid="tasks-filter-search" />
          </div>
          <Select value={filterVrsta} onValueChange={setFilterVrsta}>
            <SelectTrigger className="bg-white border-slate-300" data-testid="tasks-filter-vrsta">
              <SelectValue placeholder="Vrsta posla" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve vrste posla</SelectItem>
              {Object.entries(vrstaPoslaMap).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterHitnost} onValueChange={setFilterHitnost}>
            <SelectTrigger className="bg-white border-slate-300" data-testid="tasks-filter-hitnost">
              <SelectValue placeholder="Hitnost" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sva hitnost</SelectItem>
              <SelectItem value="hitno">HITNO</SelectItem>
              <SelectItem value="nije_hitno">NIJE HITNO</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="bg-white border-slate-300" data-testid="tasks-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Svi statusi</SelectItem>
              <SelectItem value="novo">Novo</SelectItem>
              <SelectItem value="u_toku">U toku</SelectItem>
              <SelectItem value="zavrseno">Završeno</SelectItem>
              <SelectItem value="ceka_klijenta">Čeka klijenta</SelectItem>
            </SelectContent>
          </Select>
          {canCreate && (
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger className="bg-white border-slate-300" data-testid="tasks-filter-user">
                <SelectValue placeholder="Dodijeljeno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Svi korisnici</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role] || u.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64"><p className="text-slate-500">Učitavanje...</p></div>
      ) : sortedTasks.length === 0 ? (
        <Card className="p-8 text-center" data-testid="tasks-empty">
          <CheckSquare size={48} weight="duotone" className="text-slate-400 mx-auto mb-4" />
          <p className="text-slate-500">{tasks.length === 0 ? 'Nema zadataka' : 'Nema rezultata za filtere'}</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600" data-testid="tasks-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Hitnost</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Vrsta posla</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Naslov</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Poslao</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Dodijeljeno</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Rok</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold tracking-wider uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((task) => (
                  <tr key={task.id} className={`border-b border-slate-200 hover:bg-slate-50 ${task.hitnost === 'hitno' ? 'bg-red-50/40' : ''}`} data-testid={`task-row-${task.id}`}>
                    <td className="px-4 py-3">
                      {task.hitnost === 'hitno' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-600 text-white" data-testid={`task-hitno-badge-${task.id}`}>
                          <Lightning size={12} weight="fill" />
                          HITNO
                        </span>
                      ) : (
                        <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                          NIJE HITNO
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`task-vrsta-${task.id}`}>
                        {vrstaPoslaMap[task.vrsta_posla] || task.vrsta_posla || 'PDV'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{task.naslov}</p>
                      {task.opis && <p className="text-xs text-slate-500 mt-1 max-w-[200px] truncate">{task.opis}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm">{clients.find(c => c.id === task.client_id)?.skraceni_naziv || clients.find(c => c.id === task.client_id)?.naziv_firme || '-'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-slate-600">{task.created_by_name || getUserName(task.created_by) || '-'}</td>
                    <td className="px-4 py-3 text-sm font-medium">{getUserName(task.dodijeljeno)}</td>
                    <td className="px-4 py-3 text-xs">{task.rok ? formatDate(task.rok) : '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${getStatusColor(task.status)}`}>
                        {getStatusLabel(task.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => handleEdit(task)} className="p-2 hover:bg-slate-200 rounded transition-colors" data-testid={`task-edit-${task.id}`}>
                          <Pencil size={16} className="text-slate-600" />
                        </button>
                        {((user.role === 'admin' || user.role === 'super_admin' || user.role === 'supervizor')) && (
                          <button onClick={() => handleDelete(task.id)} className="p-2 hover:bg-red-100 rounded transition-colors" data-testid={`task-delete-${task.id}`}>
                            <Trash size={16} className="text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="task-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium text-slate-900">
              {editingTask ? 'Uredi zadatak' : 'Novi zadatak'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Vrsta posla */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Vrsta posla *</Label>
              <Select value={formData.vrsta_posla} onValueChange={(val) => setFormData({ ...formData, vrsta_posla: val })}>
                <SelectTrigger className="bg-white border-slate-300" data-testid="task-vrsta-select"><SelectValue placeholder="Izaberite vrstu posla" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(vrstaPoslaMap).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Naslov *</Label>
              <Input value={formData.naslov} onChange={(e) => setFormData({ ...formData, naslov: e.target.value })} className="bg-white border-slate-300 text-sm" required data-testid="task-naslov-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Opis</Label>
              <textarea value={formData.opis} onChange={(e) => setFormData({ ...formData, opis: e.target.value })} className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="task-opis-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Klijent</Label>
                <ClientAutocomplete clients={clients} value={formData.client_id} onChange={(id) => setFormData({ ...formData, client_id: id })} testid="task-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Dodijeljeno</Label>
                <Select value={formData.dodijeljeno || 'none'} onValueChange={(val) => setFormData({ ...formData, dodijeljeno: val === 'none' ? '' : val })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="task-user-select"><SelectValue placeholder="Izaberite korisnika" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Nedodijeljeno --</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role] || u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Hitnost toggle */}
            <div className="space-y-2">
              <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Hitnost</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, hitnost: 'hitno' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold border-2 transition-all ${
                    formData.hitnost === 'hitno'
                      ? 'bg-red-600 text-white border-red-600 shadow-md'
                      : 'bg-white text-red-600 border-red-300 hover:border-red-500'
                  }`}
                  data-testid="task-hitno-button"
                >
                  <Lightning size={18} weight="fill" />
                  HITNO
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, hitnost: 'nije_hitno' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold border-2 transition-all ${
                    formData.hitnost === 'nije_hitno'
                      ? 'bg-slate-700 text-white border-slate-700 shadow-md'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'
                  }`}
                  data-testid="task-nije-hitno-button"
                >
                  NIJE HITNO
                </button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Prioritet</Label>
                <Select value={formData.prioritet} onValueChange={(val) => setFormData({ ...formData, prioritet: val })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="task-prioritet-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nizak">Nizak</SelectItem>
                    <SelectItem value="srednji">Srednji</SelectItem>
                    <SelectItem value="visok">Visok</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Rok</Label>
                <DateInput value={formData.rok} onChange={(e) => setFormData({ ...formData, rok: e.target.value })} className="border-slate-300 text-sm" data-testid="task-rok-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Status</Label>
                <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="task-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="novo">Novo</SelectItem>
                    <SelectItem value="u_toku">U toku</SelectItem>
                    <SelectItem value="zavrseno">Završeno</SelectItem>
                    <SelectItem value="ceka_klijenta">Čeka klijenta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-md px-4 py-2 font-medium">Odustani</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium" data-testid="task-submit-button">
                {editingTask ? 'Ažuriraj' : 'Kreiraj'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
